import React, { useEffect, useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { getAPIBaseURL } from "../../services/helpers"
import { getData, updateData, deleteData } from "../../services/request"

const infoRow = (label, value) => (
  <div className="grid grid-cols-3 gap-4 border-b border-gray-100 py-3 text-sm">
    <div className="col-span-1 font-medium text-gray-600">{label}</div>
    <div className="col-span-2 text-gray-900">{value || '—'}</div>
  </div>
)

const AdminPatientDetails = () => {
  const { userId } = useParams()
  const navigate = useNavigate()
  const [patient, setPatient] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [genMsg, setGenMsg] = useState("")

  // Edit form state
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [state, setState] = useState("")
  const [localGovernment, setLocalGovernment] = useState("")
  const [address, setAddress] = useState("")
  const [states, setStates] = useState([])
  const [localGovernments, setLocalGovernments] = useState([])
  const [loadingStates, setLoadingStates] = useState(true)
  const [loadingLGs, setLoadingLGs] = useState(false)

  // Action states
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const fetchPatient = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/users`
    const params = { userId }
    const res = await getData(url, {}, params)
    setLoading(false)
    if(res.error == ""){
      if(res.result.status === 200){
        const data = res.result.data?.data
        const record = Array.isArray(data) ? data[0] : data
        setPatient(record || null)
        if(record){
          setName(record.name || "")
          setEmail(record.email || "")
          setPhoneNumber(record.phoneNumber || "")
          setState(record.state?.toString() || "")
          setLocalGovernment(record.localGovernment?.toString() || "")
          setAddress(record.address || "")
        }
        if(!record){
          setError('Patient not found')
        }
      }else{
        setError(res.result.data?.message || 'Patient not found')
      }
    }else{
      setError(res.result?.data?.message || 'Network error')
    }
  }

  useEffect(() => {
    fetchPatient()
    fetchStates()
  }, [userId])

  useEffect(() => {
    if (state) {
      fetchLocalGovernments(state)
    } else {
      setLocalGovernments([])
      setLocalGovernment("")
    }
  }, [state])

  const fetchStates = async () => {
    try {
      const url = `${getAPIBaseURL()}/v1/states`
      const res = await getData(url, {}, {})
      if (res.error == "") {
        if (res.result.status === 200) {
          setStates(res.result.data?.data || [])
        }
      }
      setLoadingStates(false)
    } catch (error) {
      setLoadingStates(false)
    }
  }

  const fetchLocalGovernments = async (stateCode) => {
    try {
      setLoadingLGs(true)
      const url = `${getAPIBaseURL()}/v1/localgovernments`
      const params = { stateCode }
      const res = await getData(url, {}, params)
      if (res.error == "") {
        if (res.result.status === 200) {
          setLocalGovernments(res.result.data?.data || [])
        }
      }
      setLoadingLGs(false)
    } catch (error) {
      setLoadingLGs(false)
    }
  }

  const handleEdit = () => {
    setEditing(true)
    setError("")
    setGenMsg("")
  }

  const handleCancelEdit = () => {
    setEditing(false)
    if (patient) {
      setName(patient.name || "")
      setEmail(patient.email || "")
      setPhoneNumber(patient.phoneNumber || "")
      setState(patient.state?.toString() || "")
      setLocalGovernment(patient.localGovernment?.toString() || "")
      setAddress(patient.address || "")
    }
  }

  const handleUpdate = async () => {
    setError("")
    setGenMsg("")
    if (!name || name.trim().length < 2) {
      setError("Name must be at least 2 characters")
      return
    }
    if (!email || !email.includes('@')) {
      setError("Valid email is required")
      return
    }
    if (!phoneNumber || phoneNumber.length < 10) {
      setError("Valid phone number is required")
      return
    }
    if (!state) {
      setError("State is required")
      return
    }
    if (!localGovernment) {
      setError("Local Government is required")
      return
    }
    if (!address || address.trim().length < 5) {
      setError("Address must be at least 5 characters")
      return
    }

    setSubmitting(true)
    const url = `${getAPIBaseURL()}/v1/users/update-account`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('userId', userId)
    params.append('name', name.trim())
    params.append('email', email.trim().toLowerCase())
    params.append('phoneNumber', phoneNumber.trim())
    params.append('state', state)
    params.append('localGovernment', localGovernment)
    params.append('address', address.trim())

    const res = await updateData(url, headers, params.toString())
    setSubmitting(false)

    if (res.error == "") {
      if (res.result.status === 200) {
        setGenMsg("Patient updated successfully")
        setEditing(false)
        fetchPatient()
      } else {
        setError(res.result.data?.message || 'Could not update patient')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const handleToggleStatus = async () => {
    setError("")
    setGenMsg("")
    const newStatus = patient.status === 1 ? 0 : 1

    setSubmitting(true)
    const url = `${getAPIBaseURL()}/v1/users/status`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('userId', userId)
    params.append('status', newStatus.toString())

    const res = await updateData(url, headers, params.toString())
    setSubmitting(false)

    if (res.error == "") {
      if (res.result.status === 200) {
        setGenMsg(`Patient ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`)
        fetchPatient()
      } else {
        setError(res.result.data?.message || 'Could not update status')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete patient "${patient?.name}"? This action cannot be undone.`)) {
      return
    }

    setError("")
    setGenMsg("")
    setDeleting(true)
    const url = `${getAPIBaseURL()}/v1/users`
    const res = await deleteData(url, {}, { userId })
    setDeleting(false)

    if (res.error == "") {
      if (res.result.status === 200) {
        navigate("/admin/app/patients")
      } else {
        setError(res.result.data?.message || 'Could not delete patient')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (e) {
      return iso
    }
  }

  return (
    <section className="mx-auto max-w-4xl space-y-4">
      <Link to="/admin/app/patients" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900">
        ← Back to patients
      </Link>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-5">
          <h1 className="text-2xl font-bold text-gray-900">Patient Details</h1>
          <p className="mt-1 text-sm text-gray-600">User ID: {userId}</p>
        </div>

        <div className="px-6 py-6">
          {loading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : error && !patient ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">{error}</div>
          ) : !patient ? (
            <div className="text-sm text-gray-500">Patient not found.</div>
          ) : (
            <>
              {error ? (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">{error}</div>
              ) : null}
              {genMsg ? (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-700">{genMsg}</div>
              ) : null}

              {editing ? (
                <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="text-sm font-semibold text-gray-900">Edit Patient Details</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-900">Name *</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-900">Email *</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-900">Phone Number *</label>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-900">State *</label>
                      {loadingStates ? (
                        <div className="block w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500">Loading...</div>
                      ) : (
                        <select
                          value={state}
                          onChange={(e) => setState(e.target.value)}
                          className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                        >
                          <option value="">Select State</option>
                          {states.map((s) => (
                            <option key={s.code} value={s.code}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-900">Local Government *</label>
                      {loadingLGs ? (
                        <div className="block w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500">Loading...</div>
                      ) : (
                        <select
                          value={localGovernment}
                          onChange={(e) => setLocalGovernment(e.target.value)}
                          disabled={!state}
                          className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black disabled:bg-gray-50 disabled:text-gray-500"
                        >
                          <option value="">Select LGA</option>
                          {localGovernments.map((lg) => (
                            <option key={lg.code} value={lg.code}>
                              {lg.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-medium text-gray-900">Address *</label>
                      <textarea
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        rows={3}
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleUpdate}
                      disabled={submitting}
                      className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      {submitting ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={submitting}
                      className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-gray-100">
                  {infoRow('Name', patient.name)}
                  {infoRow('Email', patient.email)}
                  {infoRow('Phone Number', patient.phoneNumber)}
                  {infoRow('Address', patient.address)}
                  {infoRow('State', patient.state)}
                  {infoRow('Local Government', patient.localGovernment)}
                  {infoRow('Status', (
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                      patient.status === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {patient.status === 1 ? 'Active' : 'Inactive'}
                    </span>
                  ))}
                  {infoRow('Created At', formatDate(patient.createdAt))}
                  {infoRow('Updated At', formatDate(patient.updatedAt))}
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 flex flex-wrap gap-3">
                {!editing && (
                  <button
                    onClick={handleEdit}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Edit Details
                  </button>
                )}
                <button
                  onClick={handleToggleStatus}
                  disabled={submitting}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-70 ${
                    patient.status === 1
                      ? 'border border-red-200 bg-white text-red-600 hover:bg-red-50'
                      : 'border border-green-200 bg-white text-green-600 hover:bg-green-50'
                  }`}
                >
                  {submitting ? 'Updating...' : patient.status === 1 ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {deleting ? 'Deleting...' : 'Delete Patient'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  )
}

export default AdminPatientDetails
